import { useDataChannel, useRoomInfo } from '@livekit/components-react';
import { createContext, type ReactNode, use, useCallback, useEffect, useMemo, useReducer, useState } from 'react';

import { useMeetingFeatures } from '../meeting-features';
import { useLocalEmployeeId } from '../cloud-recording';
import { i18n } from '@/i18n';
import { notifyError } from '@/shared/notify';

export type LobbyRequest = { guestId: string; name: string; requestedAt: string };
type LobbyRequestsResponse = { requests: LobbyRequest[] };

export type Lobby = {
    requests: LobbyRequest[];
    admit: (guestId: string) => Promise<void>;
    reject: (guestId: string) => Promise<void>;
    admitAll: () => Promise<void>;
};

const LOBBY_TOPIC = 'meet.lobby';
const POLL_INTERVAL_MS = 3000;

const LobbyContext = createContext<Lobby | null>(null);

export function useLobby() {
    return use(LobbyContext);
}

export function LobbyProvider({ children }: Readonly<{ children: ReactNode }>) {
    const isEmployee = useLocalEmployeeId() !== null;
    const { name: roomName } = useRoomInfo();
    const { guests } = useMeetingFeatures();
    if (!isEmployee || !roomName || !guests) return <>{children}</>;
    return <EmployeeLobby roomName={roomName}>{children}</EmployeeLobby>;
}

async function fetchLobbyRequests(lobbyUrl: string, signal: AbortSignal) {
    const response = await fetch(lobbyUrl, { signal });
    if (!response.ok) throw new Error(await response.text());
    const body = (await response.json()) as LobbyRequestsResponse;
    return body.requests;
}

function EmployeeLobby({ roomName, children }: Readonly<{ roomName: string; children: ReactNode }>) {
    const [requests, setRequests] = useState<LobbyRequest[]>([]);
    const lobbyUrl = `/api/meetings/${encodeURIComponent(roomName)}/lobby`;
    const [refreshToken, requestRefresh] = useReducer((token: number) => token + 1, 0);

    useEffect(() => {
        const controller = new AbortController();
        const load = () => {
            fetchLobbyRequests(lobbyUrl, controller.signal)
                .then(setRequests)
                .catch((error: unknown) => {
                    if (!controller.signal.aborted) console.error(error);
                });
        };
        load();
        const timer = globalThis.setInterval(load, POLL_INTERVAL_MS);
        return () => {
            controller.abort();
            globalThis.clearInterval(timer);
        };
    }, [lobbyUrl, refreshToken]);

    useDataChannel(LOBBY_TOPIC, requestRefresh);

    const decide = useCallback(
        async (path: string) => {
            try {
                const response = await fetch(`${lobbyUrl}/${path}`, { method: 'POST' });
                if (!response.ok && response.status !== 404) throw new Error(await response.text());
            } catch (error) {
                notifyError(i18n.t('conference:lobby.actionError'), error);
            }
            requestRefresh();
        },
        [lobbyUrl]
    );

    const lobby = useMemo<Lobby>(
        () => ({
            requests,
            admit: (guestId) => decide(`${encodeURIComponent(guestId)}/admit`),
            reject: (guestId) => decide(`${encodeURIComponent(guestId)}/reject`),
            admitAll: () => decide('admit-all'),
        }),
        [requests, decide]
    );

    return <LobbyContext value={lobby}>{children}</LobbyContext>;
}
